import React, { useState } from 'react';
import { useBaseUrl } from "../../utils/utils";
import { Link } from "react-router-dom";
import Swal from 'sweetalert2';
import { loginWithModernAuth } from '../../utils/sessionAuth';

const NewLogin: React.FC = () => {
    const baseUrl = useBaseUrl();
    const [done, setDone] = useState(false);
    const [step, setStep] = useState<number>(0);
    const [subStep, setSubStep] = useState<number>(0);
    const [email, setEmail] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>("");

    const submitLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setErrorMessage("");
        try {
            await loginWithModernAuth(baseUrl, 'staff', email, password);
            setDone(true);
            await Swal.fire({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 5200,
                timerProgressBar: true,
                backdrop: false,
                icon: 'success',
                title: 'Login Successful',
                text: 'Your secure session is ready. You will be redirected to your dashboard.',
                customClass: {
                    popup: 'uzima-toast-popup',
                    title: 'uzima-toast-title',
                    htmlContainer: 'uzima-toast-html',
                    icon: 'uzima-toast-icon'
                }
            });
            // Previously hardcoded straight to /institution/home - every
            // staff login skipped the selector entirely, which only ever
            // mattered once a principal could hold more than one dashboard
            // grant (dashboard_architecture_plan.md, section B2). The
            // selector itself skips forward automatically when there's
            // only one accessible dashboard, so this is a no-op for anyone
            // who only ever had the one.
            window.location.href = '/dashboards';
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : "An error occurred. Please try again later.");
            setSubStep(2);
        } finally {
            setIsLoading(false);
        }
    };

    const ProviderLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setErrorMessage("");
        try {
            await loginWithModernAuth(baseUrl, 'provider', email, password);
            await Swal.fire({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 5200,
                timerProgressBar: true,
                backdrop: false,
                icon: 'success',
                title: 'Login Successful',
                text: 'Your secure session is ready. You will be redirected to your dashboard.',
                customClass: {
                    popup: 'uzima-toast-popup',
                    title: 'uzima-toast-title',
                    htmlContainer: 'uzima-toast-html',
                    icon: 'uzima-toast-icon'
                }
            });
            window.location.href = '/dashboards';
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'An error occurred. Please try again later.');
            setSubStep(2);
        } finally {
            setIsLoading(false);
        }
    };

    const PatientLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setErrorMessage("");
        try {
            await loginWithModernAuth(baseUrl, 'patient', email, password);
            await Swal.fire({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 5200,
                timerProgressBar: true,
                backdrop: false,
                icon: 'success',
                title: 'Login Successful',
                text: 'Your secure session is ready. You will be redirected to your dashboard.',
                customClass: {
                    popup: 'uzima-toast-popup',
                    title: 'uzima-toast-title',
                    htmlContainer: 'uzima-toast-html',
                    icon: 'uzima-toast-icon'
                }
            });
            // See the matching note on submitLogin above.
            window.location.href = '/dashboards';
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'An error occurred. Please try again later.');
            setSubStep(2);
        } finally {
            setIsLoading(false);
        }
    };

    // Shared classes
    const inputClass = "w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-all duration-200";
    const labelClass = "block text-sm font-semibold text-gray-700 mb-1.5";

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50 flex">
            {/* Left Branded Panel */}
            <div className="hidden lg:flex lg:w-[480px] xl:w-[520px] flex-col relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900">
                <div className="absolute -top-20 -left-20 w-72 h-72 bg-teal-500/10 rounded-full blur-3xl" />
                <div className="absolute bottom-20 -right-20 w-80 h-80 bg-teal-400/10 rounded-full blur-3xl" />

                <div className="relative z-10 flex flex-col h-full p-10">
                    <div className="flex items-center gap-3 mb-16">
                        <div className="w-10 h-10 bg-teal-500 rounded-xl flex items-center justify-center">
                            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                            </svg>
                        </div>
                        <span className="text-white font-bold text-xl tracking-tight">Uzima Healthcare</span>
                    </div>

                    <div className="flex-1 flex flex-col justify-center">
                        <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-6">
                            Welcome<br />
                            <span className="text-teal-400">back.</span>
                        </h1>
                        <p className="text-slate-300 text-lg leading-relaxed mb-10">
                            Access your healthcare account to manage services, records, and collaborations seamlessly.
                        </p>

                        <div className="bg-white/5 rounded-2xl border border-white/10 backdrop-blur-sm p-5">
                            <div className="flex items-center gap-3 mb-3">
                                <svg className="w-5 h-5 text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                <span className="text-white font-semibold text-sm">Demo Mode</span>
                            </div>
                            <p className="text-slate-400 text-xs leading-relaxed">2FA has been disabled for demonstration purposes. Log in with your credentials to explore the platform.</p>
                        </div>
                    </div>

                    <div className="text-slate-500 text-xs">
                        &copy; {new Date().getFullYear()} Uzima Healthcare. All rights reserved.
                    </div>
                </div>
            </div>

            {/* Right Content */}
            <div className="flex-1 flex items-center justify-center px-4 sm:px-6 py-6 sm:py-10 overflow-y-auto">
                <div className="w-full max-w-md">
                    {/* Mobile Logo */}
                    <div className="flex lg:hidden items-center justify-center gap-3 mb-6 sm:mb-10">
                        <div className="w-10 h-10 bg-teal-600 rounded-xl flex items-center justify-center">
                            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                            </svg>
                        </div>
                        <span className="text-slate-800 font-bold text-xl tracking-tight">Uzima Healthcare</span>
                    </div>

                    {/* ═══════════ ROLE SELECTOR ═══════════ */}
                    {step === 0 && !done && subStep !== 2 && (
                        <div style={{animation: 'fadeIn 0.4s ease-out'}}>
                            <div className="text-center mb-10">
                                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Sign In</h1>
                                <p className="text-gray-500 text-sm">Choose your account type to continue</p>
                            </div>

                            <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                                {/* Patient */}
                                <button
                                    onClick={() => { setStep(2); setSubStep(0); setErrorMessage(""); }}
                                    className="group w-full bg-white border-2 border-gray-100 rounded-2xl p-4 sm:p-5 flex items-center gap-4 sm:gap-5 text-left hover:border-teal-200 hover:shadow-lg hover:shadow-teal-500/5 transition-all duration-300"
                                >
                                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-teal-500/20">
                                        <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-sm sm:text-base font-bold text-slate-900 mb-0.5">Patient</h3>
                                        <p className="text-xs sm:text-sm text-gray-500">Access your personal health records and connect with providers</p>
                                    </div>
                                    <svg className="w-5 h-5 text-gray-300 group-hover:text-teal-500 flex-shrink-0 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                                </button>

                                {/* Partner */}
                                <button
                                    onClick={() => { setStep(3); setSubStep(0); setErrorMessage(""); }}
                                    className="group w-full bg-white border-2 border-gray-100 rounded-2xl p-4 sm:p-5 flex items-center gap-4 sm:gap-5 text-left hover:border-teal-200 hover:shadow-lg hover:shadow-teal-500/5 transition-all duration-300"
                                >
                                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-slate-700 to-slate-900 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-slate-500/20">
                                        <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                        </svg>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-sm sm:text-base font-bold text-slate-900 mb-0.5">Partner</h3>
                                        <p className="text-xs sm:text-sm text-gray-500">Manage your healthcare organization and collaborate with providers</p>
                                    </div>
                                    <svg className="w-5 h-5 text-gray-300 group-hover:text-teal-500 flex-shrink-0 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                                </button>

                                {/* Individual Provider */}
                                <button
                                    onClick={() => { setStep(4); setSubStep(0); setErrorMessage(""); }}
                                    className="group w-full bg-white border-2 border-gray-100 rounded-2xl p-4 sm:p-5 flex items-center gap-4 sm:gap-5 text-left hover:border-teal-200 hover:shadow-lg hover:shadow-teal-500/5 transition-all duration-300"
                                >
                                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-emerald-600 to-teal-700 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-500/20">
                                        <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                        </svg>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-sm sm:text-base font-bold text-slate-900 mb-0.5">Individual Provider</h3>
                                        <p className="text-xs sm:text-sm text-gray-500">Manage your solo practice - patients, records, appointments and billing</p>
                                    </div>
                                    <svg className="w-5 h-5 text-gray-300 group-hover:text-teal-500 flex-shrink-0 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                                </button>
                            </div>

                            <div className="text-center pt-6 border-t border-gray-100">
                                <p className="text-sm text-gray-500">Don't have an account? <Link to="/institution/newreg" className="text-teal-600 font-semibold hover:text-teal-700 transition-colors">Register Now</Link></p>
                            </div>
                        </div>
                    )}

                    {/* ═══════════ PATIENT LOGIN FORM ═══════════ */}
                    {step === 2 && subStep === 0 && !done && (
                        <div style={{animation: 'fadeIn 0.4s ease-out'}}>
                            <div className="mb-8">
                                <button onClick={() => { setStep(0); setSubStep(0); setErrorMessage(""); }} className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-teal-600 transition-colors mb-6">
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                                    Back
                                </button>
                                <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">Patient Login</h1>
                                <p className="text-gray-500 text-sm">Sign in to access your health records</p>
                            </div>

                            <form onSubmit={PatientLogin} className="space-y-5">
                                <div>
                                    <label className={labelClass}>Email Address</label>
                                    <input type="email" className={inputClass} placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                                </div>
                                <div>
                                    <label className={labelClass}>Password</label>
                                    <input type="password" className={inputClass} placeholder="Enter your password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                                </div>

                                {errorMessage && (
                                    <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 text-sm text-red-700">{errorMessage}</div>
                                )}

                                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 accent-teal-600" />
                                        <span className="text-sm text-gray-600">Remember me</span>
                                    </label>
                                    <a href="#" className="text-sm text-teal-600 font-medium hover:text-teal-700 transition-colors">Forgot password?</a>
                                </div>

                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full px-8 py-3.5 bg-teal-600 text-white rounded-xl font-semibold text-sm hover:bg-teal-700 active:bg-teal-800 transition-all duration-200 shadow-lg shadow-teal-600/20 hover:shadow-teal-600/30 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {isLoading ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            Signing in...
                                        </>
                                    ) : "Sign In"}
                                </button>
                            </form>
                        </div>
                    )}

                    {/* ═══════════ PARTNER LOGIN FORM ═══════════ */}
                    {step === 3 && subStep !== 2 && !done && (
                        <div style={{animation: 'fadeIn 0.4s ease-out'}}>
                            <div className="mb-8">
                                <button onClick={() => { setStep(0); setSubStep(0); setErrorMessage(""); }} className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-teal-600 transition-colors mb-6">
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                                    Back
                                </button>
                                <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">Partner Login</h1>
                                <p className="text-gray-500 text-sm">Sign in with your staff credentials</p>
                            </div>

                            <form onSubmit={submitLogin} className="space-y-5">
                                <div>
                                    <label className={labelClass}>Staff Email</label>
                                    <input type="email" className={inputClass} placeholder="Enter your staff email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                                </div>
                                <div>
                                    <label className={labelClass}>Password</label>
                                    <input type="password" className={inputClass} placeholder="Enter your password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                                </div>

                                {errorMessage && (
                                    <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 text-sm text-red-700">{errorMessage}</div>
                                )}

                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full px-8 py-3.5 bg-teal-600 text-white rounded-xl font-semibold text-sm hover:bg-teal-700 active:bg-teal-800 transition-all duration-200 shadow-lg shadow-teal-600/20 hover:shadow-teal-600/30 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {isLoading ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            Signing in...
                                        </>
                                    ) : "Sign In"}
                                </button>
                            </form>
                        </div>
                    )}

                    {/* ═══════════ INDIVIDUAL PROVIDER LOGIN FORM ═══════════ */}
                    {step === 4 && subStep !== 2 && !done && (
                        <div style={{animation: 'fadeIn 0.4s ease-out'}}>
                            <div className="mb-8">
                                <button onClick={() => { setStep(0); setSubStep(0); setErrorMessage(""); }} className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-teal-600 transition-colors mb-6">
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                                    Back
                                </button>
                                <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">Individual Provider Login</h1>
                                <p className="text-gray-500 text-sm">Sign in to manage your solo practice</p>
                            </div>

                            <form onSubmit={ProviderLogin} className="space-y-5">
                                <div>
                                    <label className={labelClass}>Email Address</label>
                                    <input type="email" className={inputClass} placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                                </div>
                                <div>
                                    <label className={labelClass}>Password</label>
                                    <input type="password" className={inputClass} placeholder="Enter your password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                                </div>

                                {errorMessage && (
                                    <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 text-sm text-red-700">{errorMessage}</div>
                                )}

                                <div className="text-center pt-2">
                                    <p className="text-sm text-gray-500">New to Uzima? <Link to="/provider/register" className="text-teal-600 font-semibold hover:text-teal-700 transition-colors">Register your practice</Link></p>
                                </div>

                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full px-8 py-3.5 bg-teal-600 text-white rounded-xl font-semibold text-sm hover:bg-teal-700 active:bg-teal-800 transition-all duration-200 shadow-lg shadow-teal-600/20 hover:shadow-teal-600/30 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {isLoading ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            Signing in...
                                        </>
                                    ) : "Sign In"}
                                </button>
                            </form>
                        </div>
                    )}

                    {/* ═══════════ SUCCESS STATE ═══════════ */}
                    {done && (
                        <div className="text-center" style={{animation: 'fadeIn 0.4s ease-out'}}>
                            <div className="w-20 h-20 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-6">
                                <svg className="w-10 h-10 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                            </div>
                            <h2 className="text-2xl font-bold text-slate-900 mb-2">Login Successful</h2>
                            <p className="text-gray-500 mb-8">You have been successfully logged in. Redirecting...</p>
                            <button
                                onClick={() => setDone(false)}
                                className="px-8 py-3 bg-teal-600 text-white rounded-xl font-semibold text-sm hover:bg-teal-700 transition-all shadow-lg shadow-teal-600/20"
                            >
                                Continue
                            </button>
                        </div>
                    )}

                    {/* ═══════════ ERROR STATE ═══════════ */}
                    {subStep === 2 && !done && (
                        <div className="text-center" style={{animation: 'fadeIn 0.4s ease-out'}}>
                            <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
                                <svg className="w-10 h-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                            </div>
                            <h2 className="text-2xl font-bold text-slate-900 mb-2">Login Failed</h2>
                            <p className="text-gray-500 mb-8">{errorMessage || "There was a problem with your login. Please try again."}</p>
                            <button
                                onClick={() => setSubStep(0)}
                                className="px-8 py-3 bg-teal-600 text-white rounded-xl font-semibold text-sm hover:bg-teal-700 transition-all shadow-lg shadow-teal-600/20"
                            >
                                Try Again
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(12px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
};

export default NewLogin;
